import React, { HTMLAttributes } from 'react';
import {
  ApartmentCard,
  arrayUtils,
  commonUtils,
  moneyUtils,
} from '@sato/components';

import { ExternalLink } from '@components/common/Link';
import { GA_EVENT_TYPES, LinkClickEvent } from '@models/analyticsEvents.types';
import { ApartmentProduct } from '@models/satofi/apartmentProduct.types';
import { SatoFIRoute } from '@routes/routes.constants';
import { localizationStore, t } from '@stores/localization';
import { sendGoogleAnalyticsEvent } from '@utils/analytics.utils';
import satoFiUtils from '@utils/satoFi.utils';
import { apartmentUtils } from '@views/ApartmentPage/ApartmentPage.utils';

import {
  formatApartmentAvailabilityText,
  getApartmentFlagKeys,
  getApartmentProductMedia,
  getApartmentPromotionText,
} from './ApartmentProductCard.utils';

type Props = {
  apartmentProduct: ApartmentProduct;
  className?: string;
  style?: HTMLAttributes<HTMLDivElement>['style'];
  /** Google analytics `link_click_event` event parameters */
  analytics?: Pick<LinkClickEvent, 'linkContext' | 'contentContext'>;
};

export const ApartmentProductCard = ({
  apartmentProduct,
  className,
  style,
  analytics,
}: Props) => {
  const { municipality, district } =
    apartmentProduct.realEstate.addresses[0] ?? {};

  const streetAddress =
    apartmentProduct.apartment.addresses[0]?.streetAddress ??
    apartmentProduct.realEstate.addresses[0]?.streetAddress;

  const rent = apartmentProduct.apartment.rent;

  const formatMoney = (rentInEuros: number) =>
    moneyUtils.formatMoney(
      { currency: 'EUR', value: rentInEuros },
      localizationStore.locale.locale
    );

  const apartmentHref = satoFiUtils.getSatoFIUrl(
    SatoFIRoute.URL_APARTMENT_PAGE,
    satoFiUtils.getRouteParamsForApartmentPage({
      language: localizationStore.language,
      productId: apartmentProduct.id,
      realEstateId: apartmentProduct.realEstate.id,
      district: district?.name ?? '',
      municipality: municipality?.name ?? '',
      streetAddress: streetAddress ?? '',
    })
  );

  return (
    <ApartmentCard
      style={style}
      wrapperComponent={ExternalLink}
      onClick={() => {
        if (!analytics) return;

        sendGoogleAnalyticsEvent({
          ...analytics,
          event: GA_EVENT_TYPES.LINK_CLICK,
          href: apartmentHref,
        } satisfies LinkClickEvent);
      }}
      to={apartmentHref}
      rent={
        !!rent && (
          <span className="flex flex-nowrap">
            {formatMoney(rent.campaign?.value ?? rent.normal.value)}

            {commonUtils.nonNullable(rent?.campaign?.value) && (
              <span className="relative ml-[10%] flex items-center px-[0.1rem]">
                <span className="opacity-70">
                  {formatMoney(rent.normal.value)}
                </span>

                <span className="absolute left-0 right-0 block h-[17%] w-full bg-brand-tertiary-main" />
              </span>
            )}
          </span>
        )
      }
      flags={getApartmentFlagKeys(apartmentProduct.apartment).map((flagKey) =>
        t(`apartmentFlags.${flagKey}`)
      )}
      className={className}
      title={streetAddress ?? ''}
      subTitles={[
        arrayUtils.joinArrayString([municipality?.name, district?.name]),
        arrayUtils.joinArrayString(
          [
            apartmentProduct.apartment.livingArea.value + '\u{00A0}m²',
            apartmentUtils.format.roomTypes(
              apartmentProduct.apartment.rooms.formatted,
              localizationStore.language
            ),
          ],
          ' · '
        ),
      ]}
      promotion={getApartmentPromotionText(apartmentProduct.apartment)}
      availability={formatApartmentAvailabilityText({
        apartment: apartmentProduct.apartment,
        translate: t,
      })}
      images={
        getApartmentProductMedia(apartmentProduct)?.map((media) => ({
          cloudinaryId: media.id,
          alt: media.description ?? '',
          fitImage: media.type === 'FLOORPLAN',
          // show white background behind floor plan image to hide picture edges
          className: media.type === 'FLOORPLAN' ? 'bg-white p-1' : undefined,
        })) ?? [{ cloudinaryId: '' }]
      }
    />
  );
};
